/**
 * Concierge Pods — Regions & countries (PROD-6787). Split out of /concierge-pods so that page
 * leads with the Concierge team. Manage geographic pods (regions), their country mappings, and
 * per-pod change history. Talks to the goserver CMS API at /v2/cms_user/concierge_pods/*.
 * Reached via the "Regions & countries »" link on /concierge-pods.
 *
 * Mobile-first (cards stack on small screens, grid on lg+); uses the dark-mode theme tokens.
 */

import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import moment from 'moment-timezone';
import CommonHeader from '../../util_components/common_header';
import CustomLoading from '../../util_components/custom_loading';
import Button from '../../util_components/button';
import CustomModal from '../../util_components/custom_modal';
import CustomInput from '../../util_components/custom_input';
import CustomSingleSelect from '../../util_components/custom_single_select';
import { get_api_url } from '../../../utils/urls';
import { get_api, post_api, put_api, delete_api } from '../../../redux/api_funcs';
import * as actions from '../../../redux/action_creators';

const BASE = '/v2/cms_user/concierge_pods';

const TZ_GROUP_OPTIONS = [
  { value: '', label: '(none)' },
  { value: 'east', label: 'East' },
  { value: 'west', label: 'West' }
];

// Theme-token styles (dark-mode aware — see src/theme/tokens.css).
const PAGE_STYLE = {
  background: 'var(--surface-page)',
  color: 'var(--text-primary)',
  minHeight: '100%'
};
const CARD_STYLE = { background: 'var(--surface-card)', borderColor: 'var(--border-default)' };
const CHIP_STYLE = {
  background: 'var(--surface-sunken)',
  color: 'var(--text-secondary)',
  borderColor: 'var(--border-default)'
};
const MUTED_STYLE = { color: 'var(--text-muted)' };
const ACTION_BTN = { padding: '6px 12px', fontSize: '13px' };

const AUDIT_ACTION_LABEL = {
  region_create: 'Created pod',
  region_update: 'Updated pod',
  country_add: 'Added country',
  country_remove: 'Removed country',
  teacher_assign: 'Assigned advisor',
  teacher_unassign: 'Removed advisor',
  limits_update: 'Updated limits'
};

class ConciergePodsRegions extends Component {
  state = {
    loading: false,
    regions: [],
    countries: [],
    // region edit modal
    show_region_modal: false,
    edit_region: null,
    region_name: '',
    region_tz_group: '',
    // add-country modal
    show_country_modal: false,
    country_region: null,
    country_code: '',
    // change-history (audit) modal
    show_audit_modal: false,
    audit_loading: false,
    audit_data: [],
    audit_title: ''
  };

  componentDidMount() {
    this.load_all();
  }

  load_all = () => {
    this.setState({ loading: true });
    Promise.all([
      get_api(get_api_url(`${BASE}/regions`), true),
      get_api(get_api_url(`${BASE}/countries`), true)
    ])
      .then(([r1, r2]) => {
        this.setState({
          regions: (r1.data && r1.data.data) || [],
          countries: (r2.data && r2.data.data) || [],
          loading: false
        });
      })
      .catch((e) => {
        this.show_error(e, 'Failed to load regions & countries');
        this.setState({ loading: false });
      });
  };

  notify = (type, msg) => {
    const { set_notification_variable } = this.props;
    set_notification_variable(true, type, msg);
  };

  show_error = (e, fallback) => {
    let msg = fallback;
    if (e && e.response && e.response.data) {
      msg = e.response.data.reason || e.response.data.message || fallback;
    }
    this.notify('error', msg);
  };

  on_field_change = (e) => {
    this.setState({ [e.target.name]: e.target.value });
  };

  // ── Regions ────────────────────────────────────────────────────────────────

  open_edit_region = (r) => {
    this.setState({
      show_region_modal: true,
      edit_region: r,
      region_name: r.name,
      region_tz_group: r.tz_group || ''
    });
  };

  close_region_modal = () => {
    this.setState({ show_region_modal: false, edit_region: null });
  };

  save_region = () => {
    const { edit_region, region_name, region_tz_group } = this.state;
    if (!region_name.trim()) {
      this.notify('error', 'Pod name is required');
      return;
    }
    const body = { name: region_name.trim(), tz_group: region_tz_group || null };
    const req = edit_region
      ? put_api(get_api_url(`${BASE}/regions/${edit_region.id}`), body, true)
      : post_api(get_api_url(`${BASE}/regions`), body, true);
    req
      .then(() => {
        this.notify('success', edit_region ? 'Pod updated' : 'Pod created');
        this.setState({ show_region_modal: false, edit_region: null });
        this.load_all();
      })
      .catch((e) => this.show_error(e, 'Failed to save pod'));
  };

  deactivate_region = (r) => {
    put_api(get_api_url(`${BASE}/regions/${r.id}`), { is_active: 0 }, true)
      .then(() => {
        this.notify('success', 'Pod deactivated');
        this.load_all();
      })
      .catch((e) => this.show_error(e, 'Failed to deactivate pod'));
  };

  // ── Countries ──────────────────────────────────────────────────────────────

  open_add_country = (r) => {
    this.setState({ show_country_modal: true, country_region: r, country_code: '' });
  };

  close_country_modal = () => {
    this.setState({ show_country_modal: false, country_region: null, country_code: '' });
  };

  add_country = () => {
    const { country_region, country_code } = this.state;
    if (!country_code) {
      this.notify('error', 'Select a country');
      return;
    }
    post_api(
      get_api_url(`${BASE}/regions/${country_region.id}/countries`),
      { codes: [country_code] },
      true
    )
      .then(() => {
        this.notify('success', 'Country added');
        this.close_country_modal();
        this.load_all();
      })
      .catch((e) => this.show_error(e, 'Failed to add country'));
  };

  remove_country = (region_id, code) => {
    delete_api(get_api_url(`${BASE}/regions/${region_id}/countries/${code}`), true)
      .then(() => {
        this.notify('success', 'Country removed');
        this.load_all();
      })
      .catch((e) => this.show_error(e, 'Failed to remove country'));
  };

  // ── Change history ───────────────────────────────────────────────────────────

  open_audit = (title, url) => {
    this.setState({
      show_audit_modal: true,
      audit_loading: true,
      audit_data: [],
      audit_title: title
    });
    get_api(get_api_url(url), true)
      .then((res) =>
        this.setState({ audit_data: (res.data && res.data.data) || [], audit_loading: false })
      )
      .catch((e) => {
        this.show_error(e, 'Failed to load change history');
        this.setState({ audit_loading: false });
      });
  };

  close_audit_modal = () => {
    this.setState({ show_audit_modal: false, audit_data: [] });
  };

  // ── Sub-renders ──────────────────────────────────────────────────────────────

  render_chip = (chip_key, label, onRemove, title) => (
    <button
      key={chip_key}
      type="button"
      onClick={onRemove}
      title={title}
      className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs border"
      style={CHIP_STYLE}
    >
      {label} <span aria-hidden="true">✕</span>
    </button>
  );

  render_regions = () => {
    const { regions } = this.state;
    return (
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        {regions.map((r) => (
          <div
            key={r.id}
            className="rounded-lg border p-4"
            style={{ ...CARD_STYLE, opacity: r.is_active ? 1 : 0.6 }}
          >
            <div className="font-semibold">
              {r.name}
              {!r.is_active && (
                <span className="ml-2 text-xs" style={MUTED_STYLE}>
                  (inactive)
                </span>
              )}
            </div>
            <div className="mt-0.5 text-xs" style={MUTED_STYLE}>
              tz_group: {r.tz_group || '—'}
            </div>
            <div className="mt-3 flex flex-wrap gap-1">
              {(r.countries || []).map((code) =>
                this.render_chip(
                  code,
                  code,
                  () => this.remove_country(r.id, code),
                  'Remove country'
                )
              )}
              {r.tz_group && (
                <span className="self-center text-xs" style={MUTED_STYLE}>
                  Americas — routed by timezone
                </span>
              )}
              {!r.tz_group && (r.countries || []).length === 0 && (
                <span className="self-center text-xs" style={MUTED_STYLE}>
                  No countries yet
                </span>
              )}
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button onClick={() => this.open_add_country(r)} custom_style={ACTION_BTN}>
                + Country
              </Button>
              <Button onClick={() => this.open_edit_region(r)} custom_style={ACTION_BTN}>
                Edit
              </Button>
              <Button
                onClick={() =>
                  this.open_audit(`${r.name} — change history`, `${BASE}/regions/${r.id}/audit`)
                }
                custom_style={ACTION_BTN}
              >
                History
              </Button>
              {!!r.is_active && (
                <Button onClick={() => this.deactivate_region(r)} custom_style={ACTION_BTN}>
                  Deactivate
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  render_region_modal = () => {
    const { show_region_modal, edit_region, region_name, region_tz_group } = this.state;
    if (!show_region_modal) return null;
    return (
      <CustomModal show close={this.close_region_modal}>
        <h3 className="mb-4 text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
          {edit_region ? 'Edit pod' : 'New pod'}
        </h3>
        <CustomInput
          label="Pod name"
          name="region_name"
          value={region_name}
          onChange={this.on_field_change}
          style={{ width: '100%' }}
        />
        <CustomSingleSelect
          label="Timezone group (Americas only)"
          options={TZ_GROUP_OPTIONS}
          name="region_tz_group"
          value={region_tz_group}
          onChange={this.on_field_change}
          style={{ width: '100%' }}
        />
        <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button onClick={this.close_region_modal}>Cancel</Button>
          <Button onClick={this.save_region}>Save</Button>
        </div>
      </CustomModal>
    );
  };

  render_country_modal = () => {
    const { show_country_modal, country_region, country_code, countries } = this.state;
    if (!show_country_modal || !country_region) return null;
    return (
      <CustomModal show close={this.close_country_modal}>
        <h3 className="mb-4 text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
          Add country to {country_region.name}
        </h3>
        <CustomSingleSelect
          label="Country"
          options={countries.map((c) => ({ value: c.code, label: `${c.name} (${c.code})` }))}
          name="country_code"
          value={country_code}
          onChange={this.on_field_change}
          style={{ width: '100%' }}
        />
        <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button onClick={this.close_country_modal}>Cancel</Button>
          <Button onClick={this.add_country}>Add</Button>
        </div>
      </CustomModal>
    );
  };

  render_audit_modal = () => {
    const { show_audit_modal, audit_loading, audit_data, audit_title } = this.state;
    if (!show_audit_modal) return null;
    return (
      <CustomModal show close={this.close_audit_modal}>
        <h3 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
          {audit_title}
        </h3>
        {audit_loading && <CustomLoading />}
        {!audit_loading && (
          <div className="mt-3 flex max-h-96 flex-col gap-2 overflow-y-auto">
            {audit_data.map((a) => (
              <div key={a.id} className="rounded border p-2" style={CARD_STYLE}>
                <div className="text-sm font-medium">
                  {AUDIT_ACTION_LABEL[a.action] || a.action}
                  {a.teacher_name ? ` — ${a.teacher_name}` : ''}
                  {a.region_name ? ` (${a.region_name})` : ''}
                </div>
                <div className="mt-0.5 text-xs" style={MUTED_STYLE}>
                  {a.detail ? `${a.detail} · ` : ''}
                  {a.actor_name || a.actor_uuid || 'unknown'} ·{' '}
                  {moment(a.created_date).tz('Asia/Kolkata').format('MMM D, YYYY h:mm A [IST]')}
                </div>
              </div>
            ))}
            {audit_data.length === 0 && (
              <div className="text-xs" style={MUTED_STYLE}>
                No changes recorded yet.
              </div>
            )}
          </div>
        )}
      </CustomModal>
    );
  };

  render() {
    const { loading } = this.state;
    return (
      <div style={PAGE_STYLE}>
        <CommonHeader title="Concierge — Regions & countries" loading={loading} />
        {loading ? (
          <CustomLoading />
        ) : (
          <div className="mx-auto max-w-5xl px-3 py-4 sm:px-6">
            <Link
              to="/concierge-pods"
              className="text-sm"
              style={{ color: 'var(--brand-primary)' }}
            >
              &larr; Back to Concierge Pods
            </Link>
            <h2 className="mb-3 mt-3 text-lg font-semibold">Regions &amp; countries</h2>
            {this.render_regions()}
            {this.render_region_modal()}
            {this.render_country_modal()}
            {this.render_audit_modal()}
          </div>
        )}
      </div>
    );
  }
}

ConciergePodsRegions.propTypes = {
  set_notification_variable: PropTypes.func.isRequired
};

const mapStateToProps = () => ({});

const mapDispatchToProps = (dispatch) => ({
  set_notification_variable: (show, type, mes) =>
    dispatch(actions.set_notification_variable(show, type, mes))
});

export default connect(mapStateToProps, mapDispatchToProps)(ConciergePodsRegions);
